/* eslint-disable react/require-default-props */
import { Bill, BillSponsor, Hearing } from '@enview/interface/types/bills/Bill';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import { getMostRecentAction, formatDate } from '../../../helpers/BillHelper';
import {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getOrganizationUser,
  getOrgForTeamMode,
  getTeamMode,
  updateOrganizationUser,
} from '../../../dux';
import { convertIdToAbbreviatedTitle, getBillAlias } from '../../../helpers/BillHelper';
import { SortByParam, SortOrder } from '../../../models/SortOrder';
import BillStatusBadge from '../../LegislativeTracking/BillStatusBadge';
import BillTagButton from '../BillTagging/BillTagButton';
import BillTrackingButton from '../BillTracking/BillTrackingButton';
import BillTableHeader, { BillTableFilterProperties } from './BillTableHeader';
import BillPosition from '../BillPosition/BillPosition';
import {
  democratPartyIdentifier,
  partyString,
  republicanPartyIdentifier,
  splitByDate,
} from '../../../utils';
import Info from '../../../components/svg/InfoCircleIcon';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params';
import { CustomFieldsAPI } from '../../../api';
import BillCustomFieldValue from '../BillCustomField/BillCustomFieldValue';
import { DefaultBillTableColumns } from '@enview/interface/types/Bill';
import { State } from '../../../dux/@types';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { isProd, SORT_BY_FOR_COLUMN } from '../../../utils/constants';
import NoBillResults from '../../LegislativeTracking/NoBillResults';
import './Tables.scss';
import PaginationBar from './PaginationBar';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import DragAlongCell from './DragAlongCell';
import DraggableTableHeader from './DraggableTableHeader';

const DEFAULT_PAGE_SIZE = 20;

type BillTableProps = {
  results: { data: any; count: number };
  searching: boolean;
  setSearching: (arg: boolean) => void;
  useServerSidePagination?: boolean;
  query: DecodedValueMap<QueryParamConfigMap>;
  setQuery: SetQuery<QueryParamConfigMap>;
  showFilterProperties: boolean;
  filterProperties?: BillTableFilterProperties;
};

//#region NewBillTable
export const NewBillTable = (props: BillTableProps): ReactElement => {
  const {
    results,
    searching,
    setSearching,
    useServerSidePagination,
    showFilterProperties,
    filterProperties,
    query,
    setQuery,
  } = props;

  const dispatch = useDispatch();
  const teamModeId = useSelector(getTeamMode);
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [loadingPage, setLoadingPage] = useState(true);
  const [tooltipAnchor, setTooltipAnchor] = useState('');
  const [tipContent, setTipContent] = useState<ReactElement<any>>(<></>);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setSearching(false);
    if (results) setLoadingPage(false);
  }, [results, setSearching]);

  //#region Column visibility + order
  const initialColumns =
    orgUser?.settings.billViewPreference?.views.billSearchView?.columns;
  const defaultColumns = Object.values(DefaultBillTableColumns);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    initialColumns ?? defaultColumns,
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(() => [
    'select-col',
    ...(initialColumns ?? defaultColumns),
  ]);

  useEffect(() => {
    setColumnOrder((prev) => {
      const prevWithoutSelect = prev.filter((id) => id !== 'select-col');

      const existing = prevWithoutSelect.filter((id) => selectedColumns.includes(id));
      const added = selectedColumns.filter((id) => !existing.includes(id));

      return ['select-col', ...existing, ...added];
    });
  }, [selectedColumns]);

  useEffect(() => {
    if (!orgUser) return;

    const orderedColumns = columnOrder.filter((id) => id !== 'select-col');

    dispatch(
      updateOrganizationUser(
        {
          ...orgUser,
          firstName: orgUser.firstName,
          lastName: orgUser.lastName,
          email: orgUser.email,
          settings: {
            ...orgUser.settings,
            billViewPreference: {
              views: {
                billSearchView: {
                  columns: orderedColumns,
                  updatedAt: Date.now().toLocaleString(),
                },
              },
            },
          },
        },
        orgUser,
      ),
    );
    dispatch(getOrganizationUser());
  }, [columnOrder]);

  const { data: customFields = [] } =
    CustomFieldsAPI.endpoints.getOrganizationCustomFields.useQuery(
      orgId ? orgId : skipToken,
    );

  //#region Pagination
  const queryPage = Number(query.page ?? 1);
  const queryPageSize = Number(query.pageSize ?? DEFAULT_PAGE_SIZE);

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: queryPage - 1,
      pageSize: queryPageSize,
    }),
    [query.page, query.pageSize],
  );

  const onPaginationChange = useCallback(
    (updater) => {
      if (!useServerSidePagination) return;

      // onPaginationChange expects updater function or value, same as setState
      const next = typeof updater === 'function' ? updater(pagination) : updater;

      const page = next.pageIndex + 1;
      const pageSize = next.pageSize;

      if (page === queryPage && pageSize === queryPageSize) return;

      setQuery({ ...query, page, pageSize });
      setLoadingPage(true);
    },
    [queryPage, queryPageSize, query, setQuery],
  );

  //#region Sorting
  const [sorting, setSorting] = useState<SortingState>(() =>
    SortOrder.getSortingFromOrderParam(query.order),
  );

  useEffect(() => {
    setSorting(SortOrder.getSortingFromOrderParam(query.order));
  }, [query.order]);

  const handleSortChange = (updater: Updater<SortingState>) => {
    setSorting((prev) => {
      // Updater is either ColumnSort[] or a function: (prev: ColumnSort[]) => ColumnSort[]
      const next = typeof updater === 'function' ? updater(prev) : updater;

      const active = next[0];

      if (active) {
        // maps column id to sort key
        const sortBy = SORT_BY_FOR_COLUMN[active.id];
        if (sortBy) {
          const orderParam = new SortOrder(sortBy, active.desc).getQueryParam();
          if (orderParam) {
            setQuery({ order: orderParam, page: 1 });
            setLoadingPage(true);
          }
        }
      } else if (query.order !== SortByParam.LATEST) {
        setQuery({ order: SortByParam.LATEST, page: 1 });
        setLoadingPage(true);
      }

      return next;
    });
  };

  //#region Bill table helpers
  const showTableResults = !searching && results && results.count > 0;

  const handleTooltipContentClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const tooltipContent = (
    primarySponsors: BillSponsor[],
    coSponsors: BillSponsor[],
  ) => {
    return (
      <div className="sponsors-tooltip" onClick={handleTooltipContentClick}>
        <span className="tooltip-header">Primary sponsors:</span>
        <ul>
          {primarySponsors.map((sponsor, index) => (
            <li key={`primary-${index}`}>
              {sponsor.person?.id ? (
                <Link to={`/person/${sponsor.person?.id}`}>
                  {`${sponsor.name}${partyString(sponsor)}`}
                </Link>
              ) : (
                `${sponsor.name}${partyString(sponsor)}`
              )}
            </li>
          ))}
        </ul>
        {!!coSponsors.length && (
          <>
            <span className="tooltip-header">Co-sponsors:</span>
            <ul>
              {coSponsors.map((sponsor, index) => (
                <li key={`co-${index}`}>
                  {sponsor.person?.id ? (
                    <Link to={`/person/${sponsor.person?.id}`}>
                      {`${sponsor.name}${partyString(sponsor)}`}
                    </Link>
                  ) : (
                    `${sponsor.name}${partyString(sponsor)}`
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderBillSponsors = (bill: Bill) => {
    const { sponsors } = bill;
    if (!sponsors) return null;

    const currentSponsors = sponsors.filter((s) => !s.isRemoved);
    const primarySponsors = currentSponsors.filter((s) => s.isPrimary);
    const coSponsors = currentSponsors.filter((s) => !s.isPrimary && !s.isRemoved);

    if (!primarySponsors.length) return null;

    const tooltipId = `table-tooltip-${bill.id}`;

    const handleSponsorFocus = () => {
      setTipContent(tooltipContent(primarySponsors, coSponsors));
      setTooltipAnchor(tooltipId);
      setShowTooltip(true);
    };

    return (
      <p
        className="bill-sponsor-table-cell"
        id={tooltipId}
        key={bill.id}
        onClick={handleSponsorFocus}
        onMouseEnter={handleSponsorFocus}
      >
        <span>{primarySponsors[0].name + partyString(primarySponsors[0])}</span>
        <Info className="ml-1" color="#4a4a4a" size={16} />
      </p>
    );
  };

  const renderUpcomingHearings = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return null;

    const { upcomingEvents, pastEvents } = splitByDate(hearings);
    let displayedEvent: Hearing;

    if (upcomingEvents.length > 0) {
      displayedEvent = upcomingEvents.reduce((nearest, curr) =>
        new Date(curr.start).getTime() < new Date(nearest.start).getTime()
          ? curr
          : nearest,
      );
    } else {
      displayedEvent = pastEvents.reduce((latest, curr) =>
        new Date(curr.start).getTime() > new Date(latest.start).getTime()
          ? curr
          : latest,
      );
    }

    const hearingDate = formatDate(displayedEvent.start, 'short');
    const upcomingHearingsId = 'upcoming-hearing' + bill.sourceBillId;
    const upcomingEventContent = `${hearingDate} - ${displayedEvent.eventData.name}`;

    const handleUpcomingHearingFocus = () => {
      setTipContent(<p>{upcomingEventContent}</p>);
      setTooltipAnchor(upcomingHearingsId);
      setShowTooltip(true);
    };

    return (
      <p
        id={upcomingHearingsId}
        onClick={handleUpcomingHearingFocus}
        onMouseEnter={handleUpcomingHearingFocus}
      >
        {upcomingEventContent}
      </p>
    );
  };

  //#region Columns
  const columns = useMemo<ColumnDef<BillSummary>[]>(() => {
    const selectionColumn: ColumnDef<BillSummary> = {
      id: 'select-col',
      size: 40,
      enableSorting: false,
      header: ({ table }) => {
        const isAllSelected = table.getIsAllRowsSelected();
        const isSomeSelected = table.getIsSomeRowsSelected();
        return (
          <input
            aria-label="Select all rows on this page"
            checked={isAllSelected}
            onChange={table.getToggleAllRowsSelectedHandler()}
            ref={(el) => {
              if (!el) return;
              el.indeterminate = !isAllSelected && isSomeSelected;
            }}
            type="checkbox"
          />
        );
      },
      cell: ({ row }) => (
        <input
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          type="checkbox"
        />
      ),
    };

    const baseColumns: ColumnDef<BillSummary>[] = [
      {
        header: 'Tracking',
        id: DefaultBillTableColumns.TRACKING,
        size: 150,
        enableSorting: false,
        minSize: 110,
        cell: ({ row }) => (
          <BillTrackingButton
            bill={row.original as Bill}
            hasStatusIndicator
            isCompactMode
          />
        ),
      },
      {
        id: DefaultBillTableColumns.TAGS,
        header: 'Tags',
        size: 50,
        accessorKey: 'tags',
        enableSorting: true,
        meta: { align: 'center' },
        cell: ({ row }) => (
          <BillTagButton bill={row.original as Bill} hasStatusIndicator />
        ),
      },
      {
        header: 'Position',
        id: DefaultBillTableColumns.POSITION,
        size: 50,
        cell: ({ row }) => <BillPosition bill={row.original as Bill} />,
      },
      {
        header: 'Bill ID',
        id: DefaultBillTableColumns.BILL_ID,
        accessorKey: 'id',
        enableSorting: true,
        maxSize: 200,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          const title = convertIdToAbbreviatedTitle(bill.id);
          const billAlias = getBillAlias(bill.userAliases, teamModeId);
          return (
            <Link
              className="bill-link"
              to={`/legislative-tracking/bill/details/${bill.id}`}
            >
              {title}
              {billAlias ? `(${billAlias.alias})` : ''}
            </Link>
          );
        },
      },
      {
        header: 'Bill Title',
        id: DefaultBillTableColumns.BILL_TITLE,
        accessorKey: 'name',
        enableSorting: true,
        minSize: 200,
        cell: ({ row }) => (row.original as Bill).name,
      },
      {
        header: 'Bill Sponsors',
        id: DefaultBillTableColumns.BILL_SPONSORS,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => renderBillSponsors(row.original as Bill),
      },
      {
        header: '(D)',
        id: DefaultBillTableColumns.DEMOCRATS,
        accessorKey: 'democrats',
        enableSorting: true,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          const { sponsors } = bill;
          if (!sponsors) return null;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === democratPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: '(R)',
        id: DefaultBillTableColumns.REPUBLICANS,
        accessorKey: 'republicans',
        enableSorting: true,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          const { sponsors } = bill;
          if (!sponsors) return null;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === republicanPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: 'Bill Status',
        id: DefaultBillTableColumns.BILL_STATUS,
        accessorKey: 'status',
        enableSorting: true,
        minSize: 130,
        maxSize: 130,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          return (
            <div>
              <BillStatusBadge bill={bill} isCompactMode />
            </div>
          );
        },
      },
      {
        header: 'Session',
        id: DefaultBillTableColumns.SESSION,
        enableSorting: true,
        maxSize: 150,
        accessorKey: 'session.displayName',
        cell: ({ row }) => row.original.session.displayName,
      },
      {
        header: 'Latest Action',
        id: DefaultBillTableColumns.LATEST_ACTION,
        enableSorting: false,
        maxSize: 150,
        cell: ({ row }) => getMostRecentAction(row.original)?.description ?? '',
      },
      {
        header: 'Action Date',
        id: DefaultBillTableColumns.ACTION_DATE,
        enableSorting: true,
        accessorKey: 'action-date',
        minSize: 115,
        maxSize: 150,
        cell: ({ row }) => {
          const recentAction = getMostRecentAction(row.original);
          return recentAction ? formatDate(recentAction.actionDate, 'short') : '';
        },
      },
      {
        header: 'Upcoming Hearing',
        id: DefaultBillTableColumns.UPCOMING_HEARING,
        maxSize: 300,
        accessorKey: 'hearings',
        enableSorting: true,
        cell: ({ row }) => renderUpcomingHearings(row.original),
      },
    ];

    const customColumns: ColumnDef<BillSummary>[] = orgId
      ? customFields.map((field) => ({
          header: field.title,
          id: field.title,
          minSize: 230,
          cell: ({ row }) => <BillCustomFieldValue bill={row.original} field={field} />,
        }))
      : [];

    const columnMap = [...baseColumns, ...customColumns].reduce<
      Record<string, ColumnDef<BillSummary>>
    >((acc, col) => {
      if (col.id) acc[col.id] = col;
      return acc;
    }, {});

    const visibleDataColumns = selectedColumns
      .map((id) => columnMap[id])
      .filter((col): col is ColumnDef<BillSummary> => Boolean(col));

    return [selectionColumn, ...visibleDataColumns];
  }, [customFields, selectedColumns, teamModeId, orgId]);

  //#region TanStack table
  const table = useReactTable({
    data: results?.data ?? [],
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { rowSelection, pagination, sorting, columnOrder },
    // row selection
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    // pagination
    manualPagination: !!useServerSidePagination,
    onPaginationChange: onPaginationChange,
    pageCount: useServerSidePagination
      ? Math.ceil((results?.count ?? 0) / (pagination.pageSize || 1))
      : undefined,
    // sorting
    manualSorting: true,
    onSortingChange: handleSortChange,
    enableSorting: true,
    enableMultiSort: false,
    // DnD
    onColumnOrderChange: setColumnOrder,
    debugTable: !isProd,
    debugHeaders: !isProd,
    debugColumns: !isProd,
  });

  const selectedBills = table
    .getSelectedRowModel()
    .flatRows.map((r) => r.original as Bill);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((colOrder) => {
        const oldIndex = colOrder.indexOf(active.id as string);
        const newIndex = colOrder.indexOf(over.id as string);
        return arrayMove(colOrder, oldIndex, newIndex);
      });
    }
  }

  //#region UI
  return (
    <div className="list results-pane">
      <div className="result-count">
        <BillTableHeader
          filterProperties={filterProperties}
          query={query}
          results={results}
          selectedColumns={selectedColumns}
          selectedRows={selectedBills}
          setQuery={setQuery}
          setSelectedColumns={setSelectedColumns}
          showFilterProperties={showFilterProperties}
        />
      </div>

      {loadingPage && (
        <div className="table-spinner-overlay">
          <Spinner animation="border" role="status"></Spinner>
        </div>
      )}

      {showTableResults && (
        <div className="tanstack-table bill-table filtered-table">
          <ReactTooltip
            anchorId={tooltipAnchor}
            className="tooltip"
            clickable
            delayHide={1000}
            isOpen={showTooltip}
            place="bottom"
            setIsOpen={setShowTooltip}
          >
            {tipContent}
          </ReactTooltip>

          <PaginationBar pagination={pagination} results={results} table={table} />

          <div className="table-wrapper">
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableTableHeader header={header} key={header.id} />
                        ))}
                      </SortableContext>
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <SortableContext
                          items={columnOrder}
                          key={cell.id}
                          strategy={horizontalListSortingStrategy}
                        >
                          <DragAlongCell cell={cell} key={cell.id} />
                        </SortableContext>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </DndContext>
          </div>
        </div>
      )}

      {!loadingPage && (!results || results?.count === 0) && (
        <div className="container">
          <NoBillResults />
        </div>
      )}
    </div>
  );
};

export default NewBillTable;
